<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'

import { useCreditMessagesReady } from '../composables/useCreditMessagesReady'
import { useFreePlanRouteLock } from '../composables/useFreePlanRouteLock'
import { usePlanSelection } from '../composables/usePlanSelection'

import { freePlanLockedScreens } from '@/features/account'
import { useI18n } from '@/shared/i18n'
import { Button, Icon, Spinner } from '@/shared/ui'

/** New Front's `LockedScreen` caps the "also included" row so it never wraps past two lines. */
const OTHER_SCREENS_LIMIT = 7

const lock = useFreePlanRouteLock()
const planSelection = usePlanSelection()
const { t, te } = useI18n()
const messagesReady = useCreditMessagesReady('credits.freeGate.title')

const screenTitle = computed(() => {
  const key = lock.value?.screenKey
  return key ? t(`credits.comparePlans.free.locked.${key}.title`) : ''
})
// Each screen may carry its own value proposition (ORP-26799); one without falls back to the
// shared line, so a newly locked route never renders an empty paragraph.
const valueProp = computed(() => {
  const key = lock.value ? `credits.comparePlans.free.locked.${lock.value.screenKey}.valueProp` : ''
  return key && te(key) ? t(key) : t('credits.freeGate.description')
})
const otherScreens = computed(() =>
  freePlanLockedScreens()
    .filter((screen) => screen.screenKey !== lock.value?.screenKey)
    .slice(0, OTHER_SCREENS_LIMIT),
)
</script>

<template>
  <slot v-if="!lock" />
  <div v-else-if="!messagesReady" class="grid min-h-64 place-items-center">
    <Spinner>{{ t('common.loading') }}</Spinner>
  </div>
  <section
    v-else
    class="mx-auto flex w-full min-w-0 max-w-6xl flex-col items-center px-md py-16 text-center md:py-20"
    data-testid="free-plan-route-gate"
  >
    <div class="flex w-full min-w-0 flex-col items-center">
      <h1 class="m-0 text-3xl font-bold leading-tight text-primary md:text-4xl">
        {{ t('credits.freeGate.title', { screen: screenTitle }) }}
      </h1>
      <p
        class="mt-6 mb-0 w-full max-w-3xl text-lg leading-relaxed text-muted md:text-xl"
        data-testid="free-plan-route-gate-value"
      >
        {{ valueProp }}
      </p>
      <div class="mt-8 flex flex-wrap justify-center gap-sm">
        <Button size="lg" shape="rounded" @click="planSelection.request">
          {{ t('credits.freeGate.upgrade') }}
          <template #end><Icon name="arrow-right" :size="18" aria-hidden="true" /></template>
        </Button>
        <Button variant="soft" size="lg" shape="rounded" @click="planSelection.request">
          {{ t('credits.freeGate.comparePlans') }}
        </Button>
      </div>

      <div
        v-if="otherScreens.length > 0"
        class="mt-12 flex w-full flex-col items-center gap-lg border-t border-border pt-8"
      >
        <p class="m-0 text-sm font-semibold text-muted">{{ t('credits.freeGate.alsoIncluded') }}</p>
        <ul class="m-0 flex max-w-3xl list-none flex-wrap justify-center gap-sm p-0">
          <li v-for="screen in otherScreens" :key="screen.screenKey">
            <RouterLink
              class="inline-flex items-center rounded-full border border-border bg-surface px-md py-xs text-sm font-semibold text-primary shadow-sm no-underline transition-colors hover:border-primary"
              :to="{ name: screen.routeName }"
            >
              {{ t(`credits.comparePlans.free.locked.${screen.screenKey}.title`) }}
            </RouterLink>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>
